import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import config from '../../config/config';

// Import translation dictionaries
import enTranslations from '../../../public/locales/en/common.json';
import hiTranslations from '../../../public/locales/hi/common.json';
import mrTranslations from '../../../public/locales/mr/common.json';

const dictionaries = {
  en: enTranslations,
  hi: hiTranslations,
  mr: mrTranslations
};

// Thunk to fetch available languages from database
export const fetchAvailableLanguages = createAsyncThunk(
  'language/fetchAvailableLanguages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/languages`);
      const result = await response.json();
      
      if (result.success && result.data) {
        return result.data; // Array of {code, name, isDefault}
      } else {
        return rejectWithValue('Failed to fetch languages');
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Get initial language from localStorage (client-side only)
const getInitialLanguage = () => {
  // Always return 'en' during SSR to prevent hydration mismatch
  if (typeof window === 'undefined') {
    return 'en';
  }
  
  // On client-side, check localStorage
  try {
    return localStorage.getItem('LOKELECTION_CMS_LANGUAGE') || 'en';
  } catch (error) {
    
    return 'en';
  }
};

const initialLanguage = getInitialLanguage();

const initialState = {
  language: initialLanguage,
  dictionary: dictionaries[initialLanguage],
  isLoading: false,
  error: null,
  availableLanguages: []
};

export const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {
    changeLanguage: (state, action) => {
      const newLanguage = action.payload;
      
      // Allow language switching even if dictionary doesn't exist
      // Fall back to English dictionary if the language dictionary is not available
      state.language = newLanguage;
      state.dictionary = dictionaries[newLanguage] || dictionaries['en'];
      state.error = null;
      
      // Log warning if dictionary is missing (only in development)
      if (!dictionaries[newLanguage] && typeof window !== 'undefined' && config.NODE_ENV === 'development') {
        console.warn(`Translation dictionary for '${newLanguage}' not found. Falling back to English translations.`);
      }
      
      // Store in localStorage
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('LOKELECTION_CMS_LANGUAGE', newLanguage);
        } catch (error) {
          
        }
      }
    },
    setLanguageLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setLanguageError: (state, action) => {
      state.error = action.payload;
    },
    clearLanguageError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableLanguages.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAvailableLanguages.fulfilled, (state, action) => {
        state.availableLanguages = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchAvailableLanguages.rejected, (state, action) => {
        state.isLoading = false;
        // Return empty array if API fails
        state.availableLanguages = [];
      });
  },
});

// Export actions and reducer
export const { changeLanguage, setLanguageLoading, setLanguageError, clearLanguageError } = languageSlice.actions;
export default languageSlice.reducer;

// Translation helper function
export const translateText = (state, key) => {
  const keys = key.split('.');
  let value = state.language.dictionary;
  
  for (const k of keys) {
    if (value && value[k]) {
      value = value[k];
    } else {
      return key; // Return the key if translation is missing
    }
  }
  
  return value;
};
